// filepath: h:\Scholiq\reactjs\src\component\page\master\periods\useBellSound.js
import { useState, useEffect, useRef } from 'react';
import bellSounds from './bellSoundsLocal';

/**
 * Custom hook for playing bell sounds
 * @returns {Object} Functions and state for controlling bell sounds
 */
const useBellSound = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSound, setCurrentSound] = useState('standard');
  const [currentDuration, setCurrentDuration] = useState('');
  const audioRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();

    // Pre-load the audio to avoid delay when playing
    if (bellSounds.standard && bellSounds.standard.sound) {
      audioRef.current.src = bellSounds.standard.sound;
      audioRef.current.load();
    }

    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Update current duration when sound type changes
  useEffect(() => {
    if (bellSounds[currentSound]) {
      setCurrentDuration(bellSounds[currentSound].duration);
    }
  }, [currentSound]);

  /**
   * Play a bell sound
   * @param {string} soundType - The type of sound to play (standard, digital, classic, etc.)
   * @returns {Promise} Promise that resolves when the sound finishes playing
   */
  const playSound = (soundType = 'standard') => {
    return new Promise((resolve) => {
      if (!bellSounds[soundType]) {
        console.error(`Bell sound type "${soundType}" not found`);
        resolve();
        return;
      }

      // Update current sound type and duration
      setCurrentSound(soundType);
      setCurrentDuration(bellSounds[soundType].duration);

      if (audioRef.current) {
        // Stop any currently playing sound
        audioRef.current.pause();
        audioRef.current.currentTime = 0;

        // Set the new sound source
        audioRef.current.src = bellSounds[soundType].sound;

        // Ensure volume is set to audible level
        audioRef.current.volume = 1.0;

        // Load the audio before playing
        audioRef.current.load();

        setIsPlaying(true);

        // Play with proper error handling
        const playPromise = audioRef.current.play();

        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log("Sound playing successfully");
          }).catch(error => {
            console.error('Error playing bell sound:', error);
            setIsPlaying(false);
            resolve();
          });
        }

        audioRef.current.onended = () => {
          setIsPlaying(false);
          resolve();
        };
      } else {
        resolve();
      }
    });
  };

  /**
   * Stop the currently playing sound
   */
  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return {
    playSound,
    stopSound,
    isPlaying,
    currentSound,
    currentDuration
  };
};

export default useBellSound;
